import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@journ/ui/alert-dialog'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Handle, Position } from '@/libs/workflow-node-ui'
import type { Node, NodeProps } from '@/libs/workflow-node-ui'
import type { PhaseNodeData, StepNodeData } from '../application/workflow-canvas-layout'

const HANDLE_SIZE = 14

const handleStyle = {
	width: HANDLE_SIZE,
	height: HANDLE_SIZE,
	borderRadius: '50%',
	background: 'hsl(var(--border))',
	border: '2px solid hsl(var(--background))',
}

const bottomStyle = { ...handleStyle, transform: 'translate(-50%, 50%)' }
const topStyle = { ...handleStyle, transform: 'translate(-50%, -50%)' }
const leftStyle = { ...handleStyle, transform: 'translate(-50%, -50%)' }
const rightStyle = { ...handleStyle, transform: 'translate(50%, -50%)' }

const rowHandleStyle = {
	...handleStyle,
	position: 'absolute' as const,
	top: '50%',
	transform: 'translate(-50%, -50%)',
}

const rowHandleSourceStyle = {
	...handleStyle,
	position: 'absolute' as const,
	top: '50%',
	transform: 'translate(50%, -50%)',
}

const DeleteButton = ({ onConfirm, label }: { onConfirm: () => void; label: string }) => (
	<AlertDialog>
		<AlertDialogTrigger asChild>
			<Button
				variant='ghost'
				size='icon'
				className='size-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10 nodrag nopan'
				onClick={e => e.stopPropagation()}
			>
				<Icons.Delete className='size-3.5' />
			</Button>
		</AlertDialogTrigger>
		<AlertDialogContent>
			<AlertDialogHeader>
				<AlertDialogTitle>Delete {label}?</AlertDialogTitle>
				<AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel>Cancel</AlertDialogCancel>
				<AlertDialogAction onClick={onConfirm}>Delete</AlertDialogAction>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
)

const StepNode = ({ data }: NodeProps<Node<StepNodeData>>) => {
	return (
		<div
			data-id={data.isInbox ? 'inbox-node' : 'step-node'}
			className='min-w-48 max-w-72 border border-border bg-background'
		>
			<Handle
				type='target'
				position={Position.Left}
				id={`node-target-${data.stepId}`}
				style={{ ...handleStyle, position: 'absolute', left: 0, top: 20, transform: 'translate(-50%, -50%)' }}
			/>

			<div className='px-4 pt-3 pb-2'>
				<div className='flex items-start justify-between gap-2'>
					<div className={data.isInbox ? 'text-sm text-muted-foreground' : 'font-medium text-sm leading-tight'}>
						{data.name}
					</div>
					{!data.isInbox && (
						<div className='flex items-center gap-1'>
							{data.onEdit && (
								<Button
									variant='ghost'
									size='icon'
									className='size-6 text-muted-foreground hover:text-foreground hover:bg-muted/40 nodrag nopan'
									onClick={e => {
										e.stopPropagation()
										data.onEdit!(data.stepId)
									}}
								>
									<Icons.Edit className='size-3.5' />
								</Button>
							)}
							{data.onDelete && <DeleteButton onConfirm={() => data.onDelete!(data.stepId)} label='step' />}
						</div>
					)}
				</div>
				{data.description && <div className='text-xs text-muted-foreground mt-1 line-clamp-2'>{data.description}</div>}
			</div>

			{data.transitions.length > 0 && (
				<div className='border-t border-border'>
					{data.transitions.map(t => (
						<div
							key={t.id}
							className='relative flex items-center justify-between px-4 py-2 text-xs border-b border-border/50 last:border-b-0 gap-3'
						>
							<Handle
								type='target'
								position={Position.Left}
								id={`transition-target-${t.id}`}
								style={{ ...rowHandleStyle, left: 0 }}
							/>
							<span className='text-muted-foreground truncate flex-1 pl-3'>{t.label}</span>
							<Handle
								type='source'
								position={Position.Right}
								id={`transition-source-${t.id}`}
								style={{ ...rowHandleSourceStyle, right: 0 }}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const PhaseNode = ({ data }: NodeProps<Node<PhaseNodeData>>) => {
	return (
		<div
			data-id='phase-node'
			className='min-w-60 py-6 border border-border bg-muted/10 flex flex-col justify-center px-4'
		>
			<Handle type='target' position={Position.Top} id={`top-target-${data.phaseId}`} style={topStyle} />
			<Handle type='target' position={Position.Left} id={`left-target-${data.phaseId}`} style={leftStyle} />
			<div className='flex items-start justify-between gap-2'>
				<div>
					<div className='text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1'>Phase</div>
					<div className='font-semibold text-2xl font-serif leading-tight'>{data.name}</div>
					<div className='text-xs text-muted-foreground mt-1'>{data.teamName}</div>
				</div>
				<div className='flex items-center gap-1'>
					{data.onEdit && (
						<Button
							variant='ghost'
							size='icon'
							className='size-6 text-muted-foreground hover:text-foreground hover:bg-muted/40 nodrag nopan'
							onClick={e => {
								e.stopPropagation()
								data.onEdit!(data.phaseId)
							}}
						>
							<Icons.Edit className='size-3.5' />
						</Button>
					)}
					{data.onDelete && <DeleteButton onConfirm={() => data.onDelete!(data.phaseId)} label='phase' />}
				</div>
			</div>
			<Handle type='source' position={Position.Right} id={`right-source-${data.phaseId}`} style={rightStyle} />
			<Handle type='source' position={Position.Bottom} id={`bottom-source-${data.phaseId}`} style={bottomStyle} />
			<Handle type='source' position={Position.Left} id={`left-source-${data.phaseId}`} style={leftStyle} />
			<Handle type='source' position={Position.Top} id={`top-source-${data.phaseId}`} style={topStyle} />
		</div>
	)
}

export { PhaseNode, StepNode }
